/* eslint-disable no-useless-constructor */
import { tuple } from 'fp-ts/lib/function'
import { fromEither, option } from 'fp-ts/lib/Option'
import { Match, Parser, Route, Formatter, QueryValues } from 'fp-ts-routing'
import * as t from 'io-ts'

/**
 * **Note**: This is the same function as `fp-ts-routing`'s `query`, but with a different
 * type signature - to allow for optional query parameters.
 * Will match a querystring.
 *
 *
 * **Note**. Use `io-ts`'s `strict` instead of `type` otherwise excess properties won't be removed.
 *
 * @example
 * import * as t from 'io-ts'
 * import { lit, str, query, Route } from 'fp-ts-routing'
 *
 * const route = lit('accounts')
 *   .then(str('accountId'))
 *   .then(lit('files'))
 *   .then(query(t.strict({ pathparam: t.string })))
 *   .formatter.run(Route.empty, { accountId: 'testId', pathparam: '123' })
 *   .toString()
 * assert.strictEqual(route, '/accounts/testId/files?pathparam=123')
 *
 *  @since 0.4.0
 */
export function queryPartial<A, T>(
  type: t.Type<A, Partial<Record<keyof T, QueryValues>>>,
): Match<A> {
  return new Match(
    new Parser((r) =>
      option.map(fromEither(type.decode(r.query)), (query) =>
        tuple(query, new Route(r.parts, {})),
      ),
    ),
    new Formatter((r, query) => new Route(r.parts, type.encode(query))),
  )
}
